// Pure problem generation — no rendering, fully unit-testable. A problem is a
// "missing number" equation (e.g. 7 + _ = 9) with one blanked position and a
// set of option values, exactly one of which is correct.

export type Op = "+" | "-" | "*" | "/";

export interface Token {
  text: string;
  isBlank: boolean;
}

export interface Problem {
  a: number;
  b: number;
  op: Op;
  result: number;
  /** which position is hidden and must be dragged in */
  blank: "a" | "b" | "result";
  /** the value of the blanked position */
  answer: number;
  /** shuffled option values, including the answer */
  options: number[];
  /** display tokens [a, op, b, "=", result] with one flagged blank */
  tokens: Token[];
}

export interface ProblemOptions {
  /** upper bound for + / − operands & result */
  maxResult?: number;
  /** upper bound for × / ÷ factors (and the ÷ divisor & quotient) */
  maxFactor?: number;
  ops?: Op[];
  optionCount?: number;
  /** reroll so the same (a, b, op, blank) never comes up twice in a row */
  avoid?: Pick<Problem, "a" | "b" | "op" | "blank">;
}

function randInt(min: number, max: number): number {
  return min + Math.floor(Math.random() * (max - min + 1));
}

function pick<T>(arr: readonly T[]): T {
  return arr[Math.floor(Math.random() * arr.length)]!;
}

function shuffle<T>(arr: T[]): T[] {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}

export function makeProblem(opts: ProblemOptions = {}): Problem {
  let p = generate(opts);
  const avoid = opts.avoid;
  if (avoid) {
    for (
      let tries = 0;
      tries < 12 && p.a === avoid.a && p.b === avoid.b && p.op === avoid.op && p.blank === avoid.blank;
      tries++
    ) {
      p = generate(opts);
    }
  }
  return p;
}

function generate(opts: ProblemOptions): Problem {
  const maxResult = opts.maxResult ?? 10;
  const maxFactor = opts.maxFactor ?? 9;
  const ops: Op[] = opts.ops?.length ? opts.ops : ["+", "-"];
  const optionCount = opts.optionCount ?? 4;
  const op = pick(ops);

  let a: number;
  let b: number;
  let result: number;
  switch (op) {
    case "+":
      result = randInt(2, maxResult);
      a = randInt(0, result);
      b = result - a;
      break;
    case "-":
      a = randInt(1, maxResult);
      b = randInt(0, a);
      result = a - b;
      break;
    case "*":
      a = randInt(1, maxFactor);
      b = randInt(1, maxFactor);
      result = a * b;
      break;
    case "/":
    default:
      // build from a clean quotient so division never has a remainder or ÷0
      b = randInt(1, maxFactor); // divisor (never 0)
      result = randInt(1, maxFactor); // quotient
      a = b * result; // dividend
      break;
  }

  const blank = pick(["a", "b", "result"] as const);
  const answer = blank === "a" ? a : blank === "b" ? b : result;

  // Distractors. When the blank holds a PRODUCT (× result / ÷ dividend), draw
  // from neighboring table facts — (x±1)·y and x·(y±1) are the confusions kids
  // actually have, and ±small noise is beatable by parity/last-digit alone.
  // Everywhere else (and as top-up), cluster around the answer so it's plausible.
  const preferred = new Set<number>();
  const productBlank = (op === "*" && blank === "result") || (op === "/" && blank === "a");
  if (productBlank) {
    const x = op === "*" ? a : b;
    const y = op === "*" ? b : result;
    for (const n of [(x - 1) * y, (x + 1) * y, x * (y - 1), x * (y + 1)]) {
      if (n >= 0 && n !== answer) preferred.add(n);
    }
  }
  const cluster = new Set<number>();
  for (let d = 1; d <= 5; d++) {
    if (answer - d >= 0) cluster.add(answer - d);
    cluster.add(answer + d);
  }
  for (const n of preferred) cluster.delete(n);
  const distractors = shuffle([...preferred]).slice(0, optionCount - 1);
  for (const n of shuffle([...cluster])) {
    if (distractors.length >= optionCount - 1) break;
    distractors.push(n);
  }
  const options = shuffle([answer, ...distractors]);

  const tokens: Token[] = [
    { text: String(a), isBlank: blank === "a" },
    { text: op, isBlank: false },
    { text: String(b), isBlank: blank === "b" },
    { text: "=", isBlank: false },
    { text: String(result), isBlank: blank === "result" },
  ];

  return { a, b, op, result, blank, answer, options, tokens };
}
